import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useIdleTimer } from 'react-idle-timer'
import { IconArrowOutlineRight, IconArrowOutlineLeft } from 'components/Zui/Icon'
import {
    useAuth,
    useFullScreen,
    useBasePath,
    useFileDownload,
    useToggle,
    useFileFavorites,
    useFileDelete,
    useConfirm,
    useIntl,
} from 'hooks'
import { generateAllForQuery } from 'utils/helpers'
import { useGetTagsQuery } from 'services'
import FullScreen from 'components/Fullscreen'
import { remove, fileSelector } from 'features/file-view/fileSlice'
import VideoPlayer from 'features/videoPlayer/Player'
import ImageViewer from 'features/image-viewer'
import Tags from 'features/tags'
import AddToCollection from 'features/add-files-collection'
import Share from 'features/share/Share'
import FileViewHeader from 'components/FileView/FileViewHeader'
import VideoPlayerFallback from 'components/Fallback/VideoPlayer'
import { ProgressRing, InteractiveIcon } from 'components/Zui'
import { format } from 'utils/datetime'
import { useRenderFile } from './useRenderFile'

const FileView = ({ pauseTimer, resetTimer }) => {
    const { id } = useParams()
    const { t } = useIntl()
    const history = useHistory()
    const dispatch = useDispatch()
    const { isAuthorized } = useAuth()
    const [fileId, setFileId] = useState(id)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const ids = useSelector(state => fileSelector.selectIds(state))
    const fileData = useSelector(state => fileSelector.selectById(state, fileId))
    const basePath = useBasePath()
    const fileHeaderRef = useRef()
    const fileFooterRef = useRef()
    const buttonNextRef = useRef()
    const buttonPreviousRef = useRef()
    const { getConfirmation } = useConfirm()
    const [sharablePatient, setShareablePatient] = useState()
    const { isOpen: isTagOpen, onOpen: onTagOpen, onClose: onTagClose } = useToggle()
    const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useToggle()
    const {
        isOpen: isCollectionOpen,
        onOpen: onCollectionOpen,
        onClose: onCollectionClose,
    } = useToggle()
    const { download } = useFileDownload('files')
    const { deleteFiles } = useFileDelete('files')

    const { isAllFavorite, toggleFavorite } = useFileFavorites([fileData], [fileId])
    const { data, isFetching, error } = useRenderFile({
        id: fileId,
        data: fileData,
        filesCount: ids.length,
    })

    const { data: tags } = useGetTagsQuery(
        {
            query: generateAllForQuery({ type: 'files', fileIds: [id] }),
            extraArgs: { id: `f:${id}` },
        },
        {
            skip: !id || !isAuthorized,
        }
    )

    const handleClose = () =>
        history.push({
            pathname: '/surgical-cloud',
            state: 'file-view',
        })
    const fullscreen = useFullScreen()

    useEffect(() => {
        if (!ids.length) {
            handleClose()
        }
        return () => dispatch(remove())
    }, [])

    useEffect(() => {
        setIsFullscreen(fullscreen.active)
    }, [fullscreen])

    const handleDownloadFiles = () => {
        if (!fileData.isDownloadable) return
        download({
            extraOptions: { isShared: true },
            fileIds: [fileId],
        })
    }

    const handleOnIdle = () => {
        if (isFullscreen) {
            fileHeaderRef.current.style.display = 'none'
            fileFooterRef.current.style.display = 'none'
            if (buttonNextRef.current) {
                buttonNextRef.current.style.display = 'none'
            }
            if (buttonPreviousRef.current) {
                buttonPreviousRef.current.style.display = 'none'
            }
        }
    }

    const handleOnActive = () => {
        if (isFullscreen) {
            fileHeaderRef.current.style.display = 'flex'
            fileFooterRef.current.style.display = 'block'
            if (buttonNextRef.current) {
                buttonNextRef.current.style.display = 'block'
            }
            if (buttonPreviousRef.current) {
                buttonPreviousRef.current.style.display = 'block'
            }
        }
    }

    const handleTagFiles = () => {
        if (fileData.isShared) return
        onTagOpen()
    }

    const handleAddFiles = () => {
        if (fileData.isShared) return
        onCollectionOpen()
    }

    const handleShareFiles = () => {
        if (fileData.isShared) return
        setShareablePatient({
            type: 'files',
            fileIds: [fileId],
            fileType: fileData.fileType,
            name: null,
            patient: {
                id: fileData.patientId,
                name: fileData.patient?.name,
            },
        })
        onShareOpen()
    }

    useIdleTimer({
        timeout: 5000,
        onIdle: handleOnIdle,
        onActive: handleOnActive,
        debounce: 500,
    })

    const handleNext = useCallback(() => {
        const index = ids.findIndex(id => id === fileId)
        const nextFileId = ids[index + 1]
        if (nextFileId) {
            history.replace({ pathname: `${basePath}/${nextFileId}` })
            setFileId(nextFileId)
        }
    }, [fileId])

    const handlePrev = useCallback(() => {
        const index = ids.findIndex(id => id === fileId)
        const prevFileId = ids[index - 1]
        if (prevFileId) {
            history.replace({ pathname: `${basePath}/${prevFileId}` })
            setFileId(prevFileId)
        }
    }, [fileId])

    const getFileIndex = () => +ids.findIndex(id => id === fileId) + 1

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            fullscreen.enter()
        } else {
            fullscreen.exit()
        }
    }

    const handleFavorite = () => {
        if (fileData.isShared) return
        toggleFavorite([fileId])
    }

    const handleDeleteFiles = async () => {
        if (fileData.isShared) return
        const confirmed = await getConfirmation({
            title: t('patient.files.confirm.delete.title'),
            message: t('patient.files.confirm.delete.message'),
            buttons: [t('cancel'), t('delete')],
        })

        if (!confirmed) return

        deleteFiles({
            id: fileData.patientId,
            fileIds: [fileId],
        })
        handleClose()
    }

    const renderFile = useMemo(() => {
        if (isFetching)
            return (
                <div className="loading-wrapper">
                    <div className="loader-container">
                        <ProgressRing size="s24" />
                    </div>
                </div>
            )
        if (error) {
            return (
                <VideoPlayerFallback
                    handleDownload={handleDownloadFiles}
                    isDownloadable={fileData?.isDownloadable}
                    error={error?.message}
                />
            )
        }
        if (fileData?.fileType === 'video' && data) {
            return (
                <VideoPlayer
                    id={id}
                    url={data.streamingUrl}
                    token={data.authorizationToken}
                    defaultFullscreen={isFullscreen}
                    onPrevFile={handlePrev}
                    onNextFile={handleNext}
                    onToggleFullscreen={toggleFullscreen}
                    ref={fileFooterRef}
                    pauseTimer={pauseTimer}
                    resetTimer={resetTimer}
                />
            )
        }
        if (fileData?.fileType === 'image' && data) {
            return (
                <ImageViewer
                    image={data.downloadUrl}
                    onPrevFile={handlePrev}
                    onNextFile={handleNext}
                    onToggleFullscreen={toggleFullscreen}
                    defaultFullscreen={isFullscreen}
                    ref={fileFooterRef}
                    fileIndex={getFileIndex()}
                    totalFiles={ids.length}
                />
            )
        }
    }, [isFetching, fileData, isFullscreen])

    return (
        <FullScreen className="fullscreen-viewer" handle={fullscreen}>
            {ids.length > 0 && (
                <>
                    {getFileIndex() > 1 && ids.length > 1 && (
                        <div className="previous-file" ref={buttonPreviousRef}>
                            <InteractiveIcon
                                theme="zbds-dark"
                                className=" zui-circular-icon"
                                onClick={handlePrev}
                            >
                                <IconArrowOutlineLeft />
                            </InteractiveIcon>
                        </div>
                    )}

                    {getFileIndex() !== ids.length && ids.length > 1 && (
                        <div className="next-file" ref={buttonNextRef}>
                            <InteractiveIcon
                                theme="zbds-dark"
                                className=" zui-circular-icon"
                                onClick={handleNext}
                            >
                                <IconArrowOutlineRight />
                            </InteractiveIcon>
                        </div>
                    )}
                    <div className="file-viewer-wrapper" key={id}>
                        <div
                            className={`image-viewer-header ${
                                isFullscreen ? 'fullscreen-header' : ''
                            }`}
                            ref={fileHeaderRef}
                        >
                            <FileViewHeader
                                onClose={handleClose}
                                tags={tags}
                                fileType={fileData.fileType.toLowerCase()}
                                title={fileData.patient ? fileData.patient.name : null}
                                handleDownloadFiles={handleDownloadFiles}
                                handleTagFiles={handleTagFiles}
                                handleAddFiles={handleAddFiles}
                                handleShareFiles={handleShareFiles}
                                isFavorite={isAllFavorite}
                                isShared={Boolean(fileData.isShared)}
                                isDownloadable={fileData.isDownloadable}
                                handleFavorite={handleFavorite}
                                handleDeleteFiles={handleDeleteFiles}
                                upadatedDate={format(fileData.updatedDateTime, 'dd.MM.yyyy')}
                                upadatedTime={format(fileData.updatedDateTime, 'HH.mm')}
                            />
                        </div>
                        {renderFile}
                        {isTagOpen && (
                            <Tags
                                onClose={onTagClose}
                                data={{ type: 'files', fileIds: [fileId] }}
                            />
                        )}
                        {isCollectionOpen && (
                            <AddToCollection
                                onClose={onCollectionClose}
                                data={{ type: 'files', fileIds: [fileId] }}
                            />
                        )}
                        {isShareOpen && <Share onClose={onShareClose} data={sharablePatient} />}
                    </div>
                </>
            )}
        </FullScreen>
    )
}

export default FileView
